import { ReactElement, useState, useCallback, memo } from 'react';
import { Collapse, Divider, Stack } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

import { pluralize } from '~utils/commons';
import { getImportRowErrorTypeLabel } from '~utils/customer';

import { DialogImportCustomersFromCSVImportErrorsDetailGroupProps } from './types';
import {
  DialogImportCustomersFromCSVImportErrorsDetailGroupContainer,
  DialogImportCustomersFromCSVImportErrorsDetailGroupHeader,
  DialogImportCustomersFromCSVImportErrorsDetailGroupHeaderInfo,
  DialogImportCustomersFromCSVImportErrorsDetailGroupSeverityDot,
  DialogImportCustomersFromCSVImportErrorsDetailGroupReference,
  DialogImportCustomersFromCSVImportErrorsDetailGroupMeta,
  DialogImportCustomersFromCSVImportErrorsDetailGroupRows,
  DialogImportCustomersFromCSVImportErrorsDetailGroupRow,
  DialogImportCustomersFromCSVImportErrorsDetailGroupRowHeader,
  DialogImportCustomersFromCSVImportErrorsDetailGroupRowMessage,
} from './styles';

const DialogImportCustomersFromCSVImportErrorsDetailGroup = ({
  group,
}: DialogImportCustomersFromCSVImportErrorsDetailGroupProps): ReactElement => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = useCallback(() => setExpanded((prev) => !prev), []);

  const label = getImportRowErrorTypeLabel(group.errorTypeId, group.errorType);

  return (
    <DialogImportCustomersFromCSVImportErrorsDetailGroupContainer>
      <DialogImportCustomersFromCSVImportErrorsDetailGroupHeader
        expanded={expanded}
        hasErrors={group.hasErrors}
        onClick={handleToggle}
      >
        <DialogImportCustomersFromCSVImportErrorsDetailGroupHeaderInfo>
          <DialogImportCustomersFromCSVImportErrorsDetailGroupSeverityDot
            hasErrors={group.hasErrors}
          />

          <DialogImportCustomersFromCSVImportErrorsDetailGroupReference variant="body2">
            {label}
          </DialogImportCustomersFromCSVImportErrorsDetailGroupReference>

          <DialogImportCustomersFromCSVImportErrorsDetailGroupMeta variant="caption">
            {pluralize({
              singular: 'fila',
              count: group.rows.length,
              includeCount: true,
            })}
          </DialogImportCustomersFromCSVImportErrorsDetailGroupMeta>
        </DialogImportCustomersFromCSVImportErrorsDetailGroupHeaderInfo>

        {expanded ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )}
      </DialogImportCustomersFromCSVImportErrorsDetailGroupHeader>

      <Collapse in={expanded} unmountOnExit>
        <DialogImportCustomersFromCSVImportErrorsDetailGroupRows
          divider={<Divider />}
        >
          {group.rows.map((row, index) => {
            const parts = [`Fila ${row.rowNumber}`];

            if (row.clientCode) parts.push(row.clientCode);
            if (row.legalName) parts.push(row.legalName);

            return (
              <DialogImportCustomersFromCSVImportErrorsDetailGroupRow
                key={`import-error-row-${row.rowNumber}-${index}`}
              >
                <DialogImportCustomersFromCSVImportErrorsDetailGroupRowHeader variant="caption">
                  {parts.join(' · ')}
                </DialogImportCustomersFromCSVImportErrorsDetailGroupRowHeader>

                <Stack spacing={0.5}>
                  {row.messages.map((message, messageIndex) => (
                    <Stack
                      key={`import-error-row-${row.rowNumber}-message-${messageIndex}`}
                      direction="row"
                      spacing={0.5}
                    >
                      <DialogImportCustomersFromCSVImportErrorsDetailGroupRowMessage
                        variant="body2"
                        fontWeight="bold"
                        color="text.secondary"
                      >
                        -
                      </DialogImportCustomersFromCSVImportErrorsDetailGroupRowMessage>

                      <DialogImportCustomersFromCSVImportErrorsDetailGroupRowMessage
                        variant="body2"
                        color="text.primary"
                      >
                        {message}
                      </DialogImportCustomersFromCSVImportErrorsDetailGroupRowMessage>
                    </Stack>
                  ))}
                </Stack>
              </DialogImportCustomersFromCSVImportErrorsDetailGroupRow>
            );
          })}
        </DialogImportCustomersFromCSVImportErrorsDetailGroupRows>
      </Collapse>
    </DialogImportCustomersFromCSVImportErrorsDetailGroupContainer>
  );
};

export default memo(DialogImportCustomersFromCSVImportErrorsDetailGroup);
